import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import Icon from '../Icon/Icon';

import styles from './DropDownSelector.module.scss';

interface DropDownSelectorProps {
  options: { value: string; label: string | ReactNode }[];
  currentValue: number | string;
  selectedClassNames?: string;
  optionClassNames?: string;
  defaultText?: string;
  classNames?: string;
  clickHandler?: (x: number | string) => void;
}

const DropDownSelector: React.FC<DropDownSelectorProps> = ({
  options,
  currentValue,
  selectedClassNames,
  optionClassNames,
  defaultText,
  classNames,
  clickHandler,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | number | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  const adjustDropdownPosition = () => {
    if (menuRef.current && toggleRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      if (menuRect.right > viewportWidth) {
        menuRef.current.style.left = 'auto';
        menuRef.current.style.right = '0';
      } else {
        menuRef.current.style.left = '0';
        menuRef.current.style.right = 'auto';
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      adjustDropdownPosition();
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    setSelectedOption(currentValue);
  }, [currentValue]);

  const currentLabel = options.find((i) => i.value === selectedOption);

  if (!defaultText && !currentLabel) {
    return null;
  }

  return (
    <div className={classes([styles.dropdown, classNames])} ref={dropdownRef}>
      <button
        className={classes([styles.dropdownToggle, selectedClassNames])}
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded={isOpen}
        ref={toggleRef}
      >
        {currentLabel?.label || defaultText}
        <Icon
          type="chevron"
          size={16}
          className={classes([
            styles.dropdownToggleIcon,
            isOpen && styles.open,
          ])}
        />
      </button>
      {isOpen && (
        <ul className={styles.dropdownMenu} role="menu" ref={menuRef}>
          {options.map((option, index) => (
            <li
              key={index}
              role="menuitem"
              onClick={() => {
                handleOptionClick(option.value);
                clickHandler?.(index);
              }}
              className={classes([styles.dropdownItem, optionClassNames])}
            >
              {option.label}
              {option.value === selectedOption && (
                <Icon
                  type="check"
                  size={16}
                  className={styles.dropdownMenuIcon}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default observer(DropDownSelector);
